import { formatCategoryName } from '@/utilities/contentHelpers'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import styles from './CaseStudySearchForm.module.scss'

function CaseStudySearchForm({ categories, onSearch, initialSearchTerm = '', initialCategory = '' }) {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)
  const [selectedOption, setSelectedOption] = useState(initialCategory)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const listboxId = 'category-listbox'

  // Sync with URL params when they change using useEffect
  useEffect(() => {
    if (searchTerm !== initialSearchTerm) {
      setSearchTerm(initialSearchTerm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchTerm])

  useEffect(() => {
    if (selectedOption !== initialCategory) {
      setSelectedOption(initialCategory)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isOpen])

  const closeDropdown = () => {
    setIsOpen(false)
    containerRef.current?.focus()
  }

  const handleCategorySelect = (categorySlug, e) => {
    e.stopPropagation()
    setSelectedOption(categorySlug)
    onSearch(searchTerm, categorySlug)
    closeDropdown()
  }

  const handleKeyDown = (e, category = null) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault()
        if (category) {
          handleCategorySelect(category, e)
        } else {
          e.stopPropagation()
          setIsOpen((prev) => !prev)
        }
        break
      case 'Escape':
        if (isOpen) closeDropdown()
        break
    }
  }

  const router = useRouter()

  useEffect(() => {
    const handleRouteChange = () => {
      setSearchTerm('')
      setSelectedOption('')
      onSearch('', '')
    }
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events, onSearch])

  const filteredCategories = categories.nodes.filter((category) => category.name.toLowerCase() !== 'uncategorized')
  const selectedCategory = filteredCategories.find((cat) => cat.slug === selectedOption)
  const viewAllOption = { name: 'View All', slug: '' }
  const fullCategoryList = [viewAllOption, ...filteredCategories]

  return (
    <form onSubmit={(e) => e.preventDefault()} className={styles.searchForm} data-aos="fade-up" data-cy="search-filter-form">
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          onSearch(e.target.value, selectedOption)
        }}
        aria-label="Search Case Studies"
      />

      <div className={styles.customSelectContainer} ref={containerRef}>
        <div
          data-cy="category-filter-dropdown"
          className={styles.customSelectDropdown}
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-owns={listboxId}
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen((prev) => !prev)
          }}
          onKeyDown={handleKeyDown}
        >
          <div className={styles.selectedOption}>
            {selectedCategory ? (
              <span>{formatCategoryName(selectedCategory.name)}</span>
            ) : (
              <span className={styles.placeholder}>Select a category</span>
            )}
          </div>

          {isOpen && (
            <ul id={listboxId} className={styles.customOptions} role="listbox" aria-label="Categories">
              {fullCategoryList.map((category, index) => (
                <li
                  key={index}
                  className={styles.customOption}
                  role="option"
                  aria-selected={selectedOption === category.slug}
                  tabIndex={0}
                  onClick={(e) => handleCategorySelect(category.slug, e)}
                  onKeyDown={(e) => handleKeyDown(e, category.slug)}
                >
                  {formatCategoryName(category.name)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </form>
  )
}

export default CaseStudySearchForm
